import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { matchSorter } from 'match-sorter';
import { useRouter } from '@/hooks';
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Stack,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Autocomplete,
  InputLabel,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import * as state from '@/state/atoms';
import { titleCase } from '@/utils';
import { abyssColors } from '@/theme/colors';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { GridView } from './GridView';
import { PlatformOverview } from './PlatformOverview';
import { PlatformRiskConfigurationModal } from './PlatformRiskConfigurationModal';
import { PlatformsContainer, MainContainer, PlatformTotalCount } from './styles';
import {
  consolidateInsightsByCategory,
  platformMatchesDeploymentTypes,
  extractAvailableCategories,
  InsightWithFilterOptions,
  RiskCalculationConfig,
} from '@/utils/platformRiskCalculations';
import {
  updateCoatingHealthInsightsFromFleetHealthFilters,
  processFleetHealthFiltersToCoatingInsights,
} from '@/utils/processFleetHealthFilters';
import { extractFleetHealthCategories } from '@/utils/fleetHealthUtils';
import { PlatformFleetHealthModal } from './PlatformFleetHealthModal';
import {
  useAllRegionsQuery,
  useAllPlatformsQuery,
  AllPlatformsQuery,
} from '@/__generated__/graphql';

type SortByOption = 'high-to-low' | 'low-to-high';

const SORT_OPTIONS = {
  HIGH_TO_LOW: 'high-to-low' as const,
  LOW_TO_HIGH: 'low-to-high' as const,
} as const;

export const Platforms = () => {
  const [selectedContinent, setSelectedContinent] = useState('global');
  const [selectedPlatforms, setSelectedPlatforms] = useState<AllPlatformsQuery['allPlatforms']>([]);
  const [searchPlatform, setSearchPlatform] = useState<string>('');
  const setPlatformName = useSetAtom(state.platformName);

  const [regionId, setRegionId] = useState<string | undefined>('');
  const [sortBy, setSortBy] = useState<SortByOption>(SORT_OPTIONS.HIGH_TO_LOW);
  const [platformSelectedFilter, setPlatformSelectedFilter] = useAtom(state.platformSelectedFilter);
  const platformsFilters = useAtomValue(state.platformsFilters);

  const [isRiskConfigModalOpen, setIsRiskConfigModalOpen] = useState(false);
  const [selectedDeploymentTypes, setSelectedDeploymentTypes] = useState<string[]>([]);
  const [selectedDataTypes, setSelectedDataTypes] = useState<string[]>([]);
  const [categoryDataTypes, setCategoryDataTypes] = useState<Record<string, string[]>>({});
  const [isCoatingHealthModalOpen, setIsCoatingHealthModalOpen] = useState(false);

  const router = useRouter();
  const isCoatingConditionFilter = (filter: string): boolean => {
    return filter === 'coating-health';
  };
  const { loading, error, data } = useAllPlatformsQuery({
    variables: {
      regionId,
    },
    fetchPolicy: 'no-cache',
  });

  const { data: regionsData } = useAllRegionsQuery({
    fetchPolicy: 'cache-first',
  });

  const allRegions = useMemo(() => {
    return regionsData?.allRegions;
  }, [regionsData]);

  const allPlatforms = useMemo(() => {
    const platforms = data?.allPlatforms ?? [];
    const isCoatingFilter = isCoatingConditionFilter(platformSelectedFilter);

    if (isCoatingFilter) {
      return platforms.map((platform) => {
        const fleetHealthFilters = platform.fleetHealthFilters;
        if (fleetHealthFilters) {
          const hasCoatingInsights =
            platform.platformCardInsights?.some(
              (insight) => insight.group?.toLowerCase() === 'coating-health'
            ) ?? false;

          if (hasCoatingInsights && platform.platformCardInsights) {
            const updatedInsights = updateCoatingHealthInsightsFromFleetHealthFilters(
              platform.platformCardInsights,
              fleetHealthFilters,
              selectedDeploymentTypes.length > 0 ? selectedDeploymentTypes : undefined,
              Object.keys(categoryDataTypes).length > 0 ? categoryDataTypes : undefined
            );

            return {
              ...platform,
              platformCardInsights: updatedInsights,
            };
          } else {
            const newInsights = processFleetHealthFiltersToCoatingInsights(
              fleetHealthFilters,
              platform.platformCardInsights
            );

            return {
              ...platform,
              platformCardInsights: [...(platform.platformCardInsights || []), ...newInsights],
            };
          }
        }
        return platform;
      });
    }

    return platforms;
  }, [data, platformSelectedFilter, selectedDeploymentTypes, categoryDataTypes]);

  const availableCategories = useMemo(() => {
    return extractAvailableCategories(allPlatforms);
  }, [allPlatforms]);

  useEffect(() => {
    if (availableCategories.categories.length === 0) return;

    if (isCoatingConditionFilter(platformSelectedFilter)) return;

    if (selectedDeploymentTypes.length === 0) {
      setSelectedDeploymentTypes(availableCategories.categories.map((cat) => cat.toLowerCase()));
    }

    if (
      selectedDataTypes.length === 0 &&
      Object.keys(availableCategories.insightTypeMap).length > 0
    ) {
      setSelectedDataTypes(Object.keys(availableCategories.insightTypeMap));
    }

    if (Object.keys(categoryDataTypes).length === 0) {
      const initialCategoryDataTypes: Record<string, string[]> = {};
      availableCategories.categories.forEach((category) => {
        const categoryId = category.toLowerCase();
        const categorySubFilters = availableCategories.subFilters[category] || new Set();
        initialCategoryDataTypes[categoryId] = Array.from(categorySubFilters);
      });
      setCategoryDataTypes(initialCategoryDataTypes);
    }
  }, [
    availableCategories,
    selectedDeploymentTypes.length,
    selectedDataTypes.length,
    categoryDataTypes,
    platformSelectedFilter,
  ]);

  const [lastFilterType, setLastFilterType] = useState<string | null>(null);

  useEffect(() => {
    if (lastFilterType !== null && lastFilterType !== platformSelectedFilter) {
      setCategoryDataTypes({});
      setSelectedDeploymentTypes([]);
      setSelectedDataTypes([]);
    }
    setLastFilterType(platformSelectedFilter);
  }, [platformSelectedFilter, lastFilterType]);

  useEffect(() => {
    if (!isCoatingConditionFilter(platformSelectedFilter)) return;
    if (!data?.allPlatforms || data.allPlatforms.length === 0) return;

    if (Object.keys(categoryDataTypes).length > 0) return;

    const extractedData = extractFleetHealthCategories(data.allPlatforms);

    if (extractedData.categories.length === 0) return;

    const initialCategoryDataTypes: Record<string, string[]> = {};
    const deploymentTypes: string[] = [];

    extractedData.categories.forEach((category) => {
      if (category.type === 'deployment') {
        deploymentTypes.push(category.id);
        initialCategoryDataTypes[category.id] = Array.from(category.availableRiLevels);
      }
    });

    setCategoryDataTypes(initialCategoryDataTypes);
    setSelectedDeploymentTypes(deploymentTypes);

    const allRiLevels = new Set<string>();
    Object.values(initialCategoryDataTypes).forEach((riLevels) =>
      riLevels.forEach((ri) => allRiLevels.add(ri))
    );
    setSelectedDataTypes(Array.from(allRiLevels));
  }, [data?.allPlatforms, platformSelectedFilter, categoryDataTypes]);

  const filteredPlatforms = useMemo(() => {
    const isCoatingFilter = isCoatingConditionFilter(platformSelectedFilter);

    if (isCoatingFilter) {
      if (selectedDeploymentTypes.length === 0) {
        // Show all platforms, regardless of whether they have fleetHealthFilters
        return allPlatforms;
      }
      return allPlatforms.filter((platform) => {
        const fleetHealthFilters = platform.fleetHealthFilters;
        // Include platforms without fleetHealthFilters
        if (!fleetHealthFilters) return true;

        // For platforms with fleetHealthFilters, check if they match selected deployment types
        return selectedDeploymentTypes.some((deploymentType) =>
          Object.keys(fleetHealthFilters).includes(deploymentType)
        );
      });
    }

    return allPlatforms.filter((platform) =>
      platformMatchesDeploymentTypes(platform, selectedDeploymentTypes)
    );
  }, [allPlatforms, platformSelectedFilter, selectedDeploymentTypes]);

  useEffect(() => {
    if (searchPlatform) {
      const filtered = filteredPlatforms.filter(
        (platform) =>
          matchSorter([platform.name], searchPlatform, {
            threshold: matchSorter.rankings.WORD_STARTS_WITH,
          }).length > 0
      );
      setSelectedPlatforms(filtered);
    } else if (selectedContinent !== 'global') {
      const filtered = filteredPlatforms.filter((platform) =>
        platform.name.toLowerCase().includes(selectedContinent.toLowerCase())
      );
      setSelectedPlatforms(filtered);
    } else {
      setSelectedPlatforms(filteredPlatforms);
    }
  }, [filteredPlatforms, searchPlatform, selectedContinent]);

  const platformOverview = useMemo(() => {
    const insightsByPlatform = selectedPlatforms.reduce((accumulator, platform) => {
      const isCoatingFilter = isCoatingConditionFilter(platformSelectedFilter);
      const insights =
        platform.platformCardInsights?.filter((insight) => {
          if (isCoatingFilter) {
            const group = insight.group?.toLowerCase();
            return group === 'coating-health';
          }
          return insight.group.toLowerCase() === platformSelectedFilter.toLowerCase();
        }) || [];

      if (insights.length > 0) {
        if (platformSelectedFilter.toLowerCase() === 'risk score') {
          const insightsWithFilterOptions: InsightWithFilterOptions[] = insights.map((insight) => ({
            ...insight,
            filterOptions:
              (
                insight as NonNullable<
                  AllPlatformsQuery['allPlatforms'][0]['platformCardInsights']
                >[0] & { filterOptions?: string[] }
              ).filterOptions || [],
          }));

          const config: RiskCalculationConfig = {
            selectedDeploymentTypes,
            categoryDataTypes,
          };

          const consolidatedInsights = consolidateInsightsByCategory(
            insightsWithFilterOptions,
            config
          );

          const chartInsights = consolidatedInsights.map((insight) => ({
            name: insight.name,
            value: insight.value,
            displayConfig: {
              color: insight.displayConfig?.color || 'defaultColor',
            },
          }));

          accumulator[platform.name] = chartInsights;
        } else if (platformSelectedFilter.toLowerCase() === 'coating-health') {
          const selectedRiLevels = new Set<string>();
          Object.values(categoryDataTypes).forEach((riLevels) => {
            riLevels.forEach((ri) => selectedRiLevels.add(ri.toLowerCase()));
          });

          const filteredInsights =
            selectedRiLevels.size > 0
              ? insights.filter((insight) => {
                  const insightName = insight.name.toLowerCase();
                  return selectedRiLevels.has(insightName);
                })
              : insights;

          const chartInsights = filteredInsights.map((insight) => ({
            name: insight.name,
            value: insight.value,
            displayConfig: {
              color: insight.displayConfig?.color || 'defaultColor',
            },
          }));

          accumulator[platform.name] = chartInsights;
        } else {
          const chartInsights = insights.map((insight) => ({
            name: insight.name,
            value: insight.value,
            displayConfig: {
              color: insight.displayConfig?.color || 'defaultColor',
            },
          }));

          accumulator[platform.name] = chartInsights;
        }
      }
      return accumulator;
    }, {} as Record<string, { name: string; value: string; displayConfig: { color: string } }[]>);

    const legendMap: { [key: string]: string } = {};

    Object.values(insightsByPlatform)
      .flat()
      .forEach(({ name, displayConfig }) => {
        if (!legendMap[name]) {
          legendMap[name] = displayConfig.color;
        }
      });

    const legendsData = legendMap;

    const summedMetricsArray = Object.values(insightsByPlatform)
      .flat()
      .reduce((accumulator, { name, value }) => {
        const numericValue = Number.parseFloat(value);

        if (accumulator[name]) {
          accumulator[name][name] =
            Number.parseFloat(String(accumulator[name][name])) + numericValue;
        } else {
          accumulator[name] = { name: name, [name]: numericValue };
        }

        return accumulator;
      }, {} as Record<string, Record<string, number | string>>);

    const summedMetrics = Object.values(summedMetricsArray);

    const metricsByPlatform = Object.entries(insightsByPlatform)
      .map(([platformName, insights]) => {
        if (platformSelectedFilter.toLowerCase() === 'coating-health') {
          const total = insights.reduce((sum, { value }) => sum + Number.parseFloat(value), 0);

          if (total === 0) {
            return { name: platformName } as Record<string, string | number>;
          }

          const result = insights.reduce(
            (accumulator, { name, value }) => {
              const numericValue = Number.parseFloat(value);
              if (numericValue !== 0) {
                accumulator[name] = (numericValue / total) * 100;
                accumulator[`__raw_${name}`] = numericValue;
              }
              return accumulator;
            },
            { name: platformName } as Record<string, string | number>
          );

          return result;
        } else {
          return insights.reduce(
            (accumulator, { name, value }) => {
              const numericValue = Number.parseFloat(value);
              if (numericValue !== 0) {
                accumulator[name] = numericValue;
              }
              return accumulator;
            },
            { name: platformName } as Record<string, string | number>
          );
        }
      })
      .filter((platform) => Object.keys(platform).length > 1);

    return {
      metricsByPlatform,
      summedMetrics,
      legendsData,
      isCoatingFilter: isCoatingConditionFilter(platformSelectedFilter),
    };
  }, [selectedPlatforms, platformSelectedFilter, selectedDeploymentTypes, categoryDataTypes]);

  const handlePlatformChange = useCallback(
    (id: string, name: string) => {
      setPlatformName(name);
      const pathname = `/platform-assets/${id}`;
      router.push({
        pathname,
      });
    },
    [router, setPlatformName]
  );

  const handleChangeContinent = (event: SelectChangeEvent) => {
    const continent = event.target.value;
    setSearchPlatform('');
    setSelectedContinent(continent);
    const id = allRegions?.find((region) => region.name.toLowerCase() === continent)?.id;
    setRegionId(id);
    setSelectedPlatforms(
      continent === 'global'
        ? allPlatforms
        : allPlatforms.filter((platform) =>
            platform.name.toLowerCase().includes(continent.toLowerCase())
          )
    );
  };

  const handleChangePlatformFilter = useCallback(
    (event: SelectChangeEvent) => {
      const filter = event.target.value as state.PlatformDisplay;
      if (filter === 'coating') setSortBy('high-to-low');
      setSearchPlatform('');
      setPlatformSelectedFilter(filter);
    },
    [setPlatformSelectedFilter]
  );

  const handleSearchPlatform = (searchTerm: string) => {
    setSearchPlatform(searchTerm);
  };

  const handleApplyFilters = useCallback(
    (deployments: string[], catDataTypes: Record<string, string[]>) => {
      setSelectedDeploymentTypes(deployments);
      setCategoryDataTypes(catDataTypes);

      const allDataTypes = new Set<string>();
      Object.values(catDataTypes).forEach((types) => types.forEach((t) => allDataTypes.add(t)));
      setSelectedDataTypes(Array.from(allDataTypes));
    },
    []
  );

  const handleApplyFleetHealthFilters = useCallback(
    (filters: {
      categories: Record<
        string,
        {
          riLevels: string[];
        }
      >;
    }) => {
      // Convert fleet health filters to categoryDataTypes format
      // Map each category to its selected RI levels
      const catDataTypes: Record<string, string[]> = {};
      Object.entries(filters.categories).forEach(([categoryId, config]) => {
        catDataTypes[categoryId] = config.riLevels;
      });

      setCategoryDataTypes(catDataTypes);

      // Extract all unique RI levels across all categories
      const allRiLevels = new Set<string>();
      Object.values(catDataTypes).forEach((riLevels) =>
        riLevels.forEach((ri) => allRiLevels.add(ri))
      );
      setSelectedDataTypes(Array.from(allRiLevels));

      // Extract only deployment types (not service groups) from the filter categories
      // Deployment types are the top-level keys in fleetHealthFilters
      const allDeploymentTypes = new Set<string>();
      allPlatforms.forEach((platform) => {
        const fleetFilters = platform.fleetHealthFilters;
        if (fleetFilters) {
          Object.keys(fleetFilters).forEach((key) => allDeploymentTypes.add(key));
        }
      });

      const deploymentTypes = Object.keys(filters.categories).filter((categoryId) => {
        return allDeploymentTypes.has(categoryId);
      });

      setSelectedDeploymentTypes(deploymentTypes);
    },
    [setCategoryDataTypes, setSelectedDataTypes, setSelectedDeploymentTypes, allPlatforms]
  );

  const hasFilteredData = selectedPlatforms.length > 0;

  return (
    <MainContainer>
      <PlatformsContainer>
        <Stack
          direction={{ sm: 'column', md: 'row' }}
          flex={1}
          spacing={3}
          alignItems="center"
          mt={7}
        >
          <Box sx={{ minWidth: { sm: '100%', md: '195px' } }}>
            <FormControl fullWidth>
              <InputLabel id="select-continent-label">Region</InputLabel>
              <Select
                inputProps={{ sx: { pt: '10px', pb: '7px' } }}
                value={selectedContinent}
                onChange={handleChangeContinent}
                data-testid="select-continent-dropdown"
                label="Region"
              >
                <MenuItem value="global">Global</MenuItem>
                {allRegions?.map((region) => (
                  <MenuItem key={region.id} value={region.name.toLowerCase()}>
                    {titleCase(region.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: { sm: '100%', md: '195px' } }}>
            <FormControl fullWidth>
              <InputLabel id="select-filter-label">Metrics</InputLabel>
              <Select
                inputProps={{ sx: { pt: '10px', pb: '7px' } }}
                value={platformSelectedFilter}
                onChange={handleChangePlatformFilter}
                data-testid="select-filter-dropdown"
                label="Metrics"
              >
                {platformsFilters?.map((filter, index) => (
                  <MenuItem key={`${filter}-${index}`} value={filter.toLowerCase()}>
                    {['risk score', 'coating-health'].includes(filter.toLowerCase()) ? (
                      <Stack direction="row" alignItems="center" gap="0.8rem">
                        <span>
                          {filter.toLowerCase() === 'risk score'
                            ? 'Score'
                            : filter.toLowerCase() === 'coating-health'
                            ? 'Coating condition'
                            : titleCase(filter)}
                        </span>
                        <AbyssIcon iconName="beta" size="sm" />
                      </Stack>
                    ) : (
                      titleCase(filter)
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {platformSelectedFilter !== 'coating' && (
            <Box flex={1} sx={{ width: '100%', maxWidth: '200px' }}>
              <FormControl fullWidth size="small">
                <InputLabel id="sort-by-label">Sort by</InputLabel>
                <Select
                  labelId="sort-by-label"
                  id="sort-by-select"
                  value={sortBy}
                  label="Sort by"
                  onChange={(event) => setSortBy(event.target.value as SortByOption)}
                >
                  <MenuItem value={SORT_OPTIONS.HIGH_TO_LOW}>Severity High to Low</MenuItem>
                  <MenuItem value={SORT_OPTIONS.LOW_TO_HIGH}>Severity Low to High</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
          <Box flex={1} sx={{ width: '100%' }}>
            <Autocomplete
              options={[...new Set(allPlatforms.map((item) => item.name))]}
              onInputChange={(event, newValue) => handleSearchPlatform(newValue ?? '')}
              freeSolo
              forcePopupIcon
              clearOnEscape
              filterOptions={(options, { inputValue }) => matchSorter(options, inputValue)}
              renderInput={(parameters) => (
                <TextField
                  {...parameters}
                  data-testid="search-platform-input"
                  sx={{
                    width: { md: '100%', xl: '640px' },
                  }}
                  size="small"
                  value={searchPlatform}
                  placeholder="Search a platform"
                  variant="outlined"
                  slotProps={{
                    input: {
                      ...parameters.InputProps,
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start" sx={{ marginLeft: '8px' }}>
                          <Search />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" data-testid="platforms-count">
            <>
              <Typography fontSize="1.4rem">Platforms found</Typography>
              <PlatformTotalCount fontSize="1.6rem" fontWeight={700}>
                {selectedPlatforms.length}
              </PlatformTotalCount>
            </>
          </Stack>
        </Stack>

        <Box my={3}>
          {(platformSelectedFilter === 'criticality' ||
            platformSelectedFilter.toLowerCase() === 'risk score' ||
            isCoatingConditionFilter(platformSelectedFilter)) && (
            <PlatformOverview
              criticalityChartTitle="Score by criticality"
              platformChartTitle={
                isCoatingConditionFilter(platformSelectedFilter)
                  ? 'Coating condition by platform'
                  : 'Score by platform'
              }
              metricsByPlatform={platformOverview.metricsByPlatform}
              summedMetrics={platformOverview.summedMetrics}
              legendsData={platformOverview.legendsData}
              totalRiskScore={''} // this is empty for now because we dont have the score yet
              sortBy={sortBy}
              showCriticalityChart={
                platformSelectedFilter.toLowerCase() !== 'risk score' &&
                !isCoatingConditionFilter(platformSelectedFilter)
              }
              isCoatingFilter={isCoatingConditionFilter(platformSelectedFilter)}
              onConfigureClick={
                platformSelectedFilter.toLowerCase() === 'risk score'
                  ? () => setIsRiskConfigModalOpen(true)
                  : platformSelectedFilter.toLowerCase() === 'coating-health'
                  ? () => setIsCoatingHealthModalOpen(true)
                  : undefined
              }
            />
          )}
        </Box>

        {!loading && !hasFilteredData && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography
              variant="body1"
              fontSize="1.6rem"
              fontWeight={500}
              color={abyssColors.primary[500]}
            >
              No risk data available for the current selection.
            </Typography>
          </Box>
        )}

        {hasFilteredData && (
          <GridView
            loading={loading}
            selectedPlatforms={selectedPlatforms}
            error={error}
            handlePlatformChange={handlePlatformChange}
            sortBy={sortBy}
            platformSelectedFilter={platformSelectedFilter}
            selectedDeploymentTypes={selectedDeploymentTypes}
            categoryDataTypes={categoryDataTypes}
          />
        )}
      </PlatformsContainer>
      {isRiskConfigModalOpen && (
        <PlatformRiskConfigurationModal
          open={isRiskConfigModalOpen}
          onClose={() => setIsRiskConfigModalOpen(false)}
          onApplyFilters={handleApplyFilters}
          selectedDeploymentTypes={selectedDeploymentTypes}
          categoryDataTypes={categoryDataTypes}
          availableCategories={availableCategories.categories}
          availableSubFilters={availableCategories.subFilters}
          insightTypeMap={availableCategories.insightTypeMap}
        />
      )}
      {platformSelectedFilter === 'coating-health' && (
        <PlatformFleetHealthModal
          open={isCoatingHealthModalOpen}
          onClose={() => setIsCoatingHealthModalOpen(false)}
          platforms={allPlatforms}
          onApplyFilters={handleApplyFleetHealthFilters}
        />
      )}
    </MainContainer>
  );
};
